import { createSelector } from 'reselect'
import { convertFromCentsToDollarsString } from 'components/FormatPrice/helpers'

import {
  isAuthenticated,
  getFullName,
  getFirstAndLastName,
  getUserRole,
} from 'selectors/user/session'
import { getPageData, getPagePath } from 'selectors/page'
import {
  getCustomerId,
  getCustomerAddressBookItemByIdOrNull,
  getCustomerEmail,
} from 'selectors/customer'
import { getUserPreferencesSelectedCurrency } from 'selectors/user/cookie'
import { getCartPayload } from 'selectors/user/cart'
import { CartPayload } from 'types/API/Easel/Cart'
import { CartItem, TCheckoutCartSlice } from './types'
import { Address } from 'types/Customer'
import { PaymentDetails } from 'components/SA/Payments/types'
import { GIFT_CARD } from 'components/Payment/helpers/constants'
import { capitalizeString } from 'lib/helpers'
import { buildGA4CartItems } from 'lib/ga4CartItems'
import { GiftCardCodeItem } from 'types/Payments/Discounts'

const { SITE_URL } = process.env

const getState = (state: Record<string, any>) => state || {}

export const getSection = createSelector([getPageData], ({ section }): string => section || 'cart')

export const getCart = (state: Record<string, any>): CartPayload =>
  getCartPayload(state as any) ?? ({} as CartPayload)

const getCheckoutCart = (state: Record<string, any>): TCheckoutCartSlice => state.cart || {}

export const getResetCart = (state: Record<string, any>): boolean => state.cart?.resetCart || false

export const getCartNotifications = createSelector(
  [getCart],
  ({ notifications }): Array<string | null | undefined> => notifications || []
)

export const getCartItems = createSelector([getCart], ({ items }): Array<CartItem> => items || [])

export const getCartItemsCount = createSelector(
  [getCart],
  ({ itemsCartCount }): number => itemsCartCount || 0
)

export const getCartSummary = createSelector(
  [getCart],
  ({
    giftCardBalance,
    giftCardAmountAppliedToCart,
    giftCardAmountAppliedToCheckout,
    isFinalSale,
    isFinalSaleOriginal,
    isFinalSalePrint,
    isFinalSaleAple,
    isFinalSaleStockIssue,
    subTotalCartPrice,
    subTotalCheckoutPrice,
    subTotalDiscountedCartPrice,
    subTotalPrice,
    totalAgentFeeAmount,
    totalCrateFeeAmount,
    totalCustomsAndDutiesAmount,
    totalDiscountAmount,
    totalHandlingFeeAmount,
    totalOrderPriceToPay,
    totalOrderPriceToPayCart,
    totalOrderPriceToPayCheckout,
    totalShippingAgentFeeAmount,
    totalShippingPrice,
    totalTaxAmount,
    totalTaxPrice,
    totalVatAmount,
  }): Record<string, any> => ({
    giftCardBalance,
    giftCardAmountAppliedToCart,
    giftCardAmountAppliedToCheckout,
    isFinalSale,
    isFinalSaleOriginal,
    isFinalSalePrint,
    isFinalSaleAple,
    isFinalSaleStockIssue,
    subTotalCartPrice,
    subTotalCheckoutPrice,
    subTotalDiscountedCartPrice,
    subTotalPrice,
    totalAgentFeeAmount,
    totalCrateFeeAmount,
    totalCustomsAndDutiesAmount,
    totalDiscountAmount,
    totalHandlingFeeAmount,
    totalOrderPriceToPay,
    totalOrderPriceToPayCart,
    totalOrderPriceToPayCheckout,
    totalShippingAgentFeeAmount,
    totalShippingPrice,
    totalTaxAmount,
    totalTaxPrice,
    totalVatAmount,
  })
)

export const getTotalOrderPriceToPayCheckout = createSelector(
  [getCartSummary],
  ({ totalOrderPriceToPayCheckout }): number => totalOrderPriceToPayCheckout || 0
)

export const getOrderNumber = createSelector(
  [getCheckoutCart],
  ({ orderNumber }): string => orderNumber || ''
)

export const getCouponCode = createSelector([getCart], ({ couponCode }): string => couponCode || '')

export const getGiftCards = createSelector(
  [getCart],
  ({ giftCards }): ReadonlyArray<GiftCardCodeItem> => giftCards || []
)

export const getGiftCardBalance = createSelector([getCart], ({ giftCardBalance }): number =>
  Number(giftCardBalance || 0)
)

export const getGiftCardCode = createSelector(
  [getCart],
  ({ giftCardCode }): ReadonlyArray<string> => giftCardCode || []
)

export const getSelectedShippingInfo = createSelector(
  [getCart],
  ({ selectedShippingInfo }) => selectedShippingInfo || {}
)

export const getAddressBookItemId = createSelector(
  [getSelectedShippingInfo],
  ({ addressBookItemId }): string => addressBookItemId || ''
)

export const getAddressGuest = createSelector([getSelectedShippingInfo], ({ guest }) => guest || '')

export const getAddressItem = createSelector(
  [getState, getAddressBookItemId],
  (state, addressBookItemId) =>
    createSelector(
      [getCustomerAddressBookItemByIdOrNull(addressBookItemId)],
      (address): Address | object => address || {}
    )(state)
)

export const getAddressItemOrNull = createSelector(
  [getState, getAddressBookItemId],
  (state, addressBookItemId) =>
    createSelector(
      [getCustomerAddressBookItemByIdOrNull(addressBookItemId)],
      (address): Address | null => address ?? null
    )(state)
)

export const getAddressItemCountryCode = createSelector(
  [getAddressItemOrNull],
  (address): string => address?.iso2Country ?? ''
)

export const getCustomerPayment = createSelector(
  [getCheckoutCart],
  ({ customerPayment }): Record<string, any> => customerPayment || {}
)

export const getWalletPaymentMethodId = createSelector(
  [getCustomerPayment],
  ({ useWallet, paymentMethodId }): string => (useWallet ? paymentMethodId : '')
)

export const getGuestPaymentFormatted = createSelector(
  [getCustomerPayment],
  (customerPayment): PaymentDetails => {
    const {
      expiration,
      expressCheckoutType,
      last4Digits,
      paymentMethodId,
      paymentMethodType,
      type,
      confirmationToken,
    } = customerPayment

    return {
      ccLast4Digits: last4Digits,
      ccType: type,
      expiration,
      expressCheckoutType: expressCheckoutType || '',
      paymentMethodId,
      paymentMethodType,
      ...(confirmationToken && { confirmationToken }),
    }
  }
)

export const getPaymentDetails = createSelector(
  [getGuestPaymentFormatted, getGiftCardCode, getTotalOrderPriceToPayCheckout],
  (customerPayment, giftCardCodes, totalPayment): PaymentDetails => {
    if (totalPayment === 0 && giftCardCodes.length > 0) {
      return {
        paymentMethodId: giftCardCodes[0],
        paymentMethodType: GIFT_CARD,
      }
    }
    return customerPayment
  }
)

export const getHasShippingAddress = createSelector(
  [getAddressBookItemId],
  (addressBookItemId): boolean => {
    return !!addressBookItemId || false
  }
)

export const getHasPaymentMethodType = createSelector(
  [getPaymentDetails],
  ({ paymentMethodType }): boolean => {
    return !!paymentMethodType || false
  }
)

export const getHasPaymentMethodId = createSelector(
  [getPaymentDetails],
  ({ paymentMethodId }): boolean => {
    return !!paymentMethodId || false
  }
)

export const getHasPayment = createSelector(
  [getHasPaymentMethodType, getHasPaymentMethodId],
  (paymentMethodType, paymentMethodId): boolean => {
    return (!!paymentMethodType && !!paymentMethodId) || false
  }
)

export const getIsCheckoutValid = createSelector(
  [getHasShippingAddress, getHasPaymentMethodType, getHasPaymentMethodId],
  (hasShippingAddress, hasPaymentMethodType, hasPaymentMethodId): boolean => {
    if (!hasShippingAddress) {
      return false
    }

    return !!hasPaymentMethodType && !!hasPaymentMethodId
  }
)

// We need to create the cart payload for place order, this is used for GUEST only
export const getPlaceOrderPayload = createSelector(
  [getCustomerEmail, getCustomerPayment, getAddressItemOrNull],
  (email, customerPayment, shippingAddress): Record<string, any> => {
    return {
      ...customerPayment,
      email: email || shippingAddress?.email,
    }
  }
)

export const getAffirmPayload = createSelector(
  [
    isAuthenticated,
    getCartItems,
    getCartSummary,
    getAddressItemOrNull,
    getCustomerId,
    getCustomerEmail,
    getFullName,
    getFirstAndLastName,
  ],
  (
    authenticated,
    cartItems,
    cartSummary,
    shippingAddress,
    customerId,
    customerEmail,
    fullName,
    firstLastData
  ): Record<string, any> => {
    const { totalOrderPriceToPayCheckout, totalShippingPrice, totalTaxAmount } = cartSummary
    const data = {
      baseUrl: SITE_URL || '',
      cartItems,
      shippingAddress,
      totalAmount: totalOrderPriceToPayCheckout,
      taxesAmount: totalTaxAmount,
      shippingAmount: totalShippingPrice,
    }

    if (authenticated) {
      const { firstName, lastName } = firstLastData
      return {
        ...data,
        customer: {
          customerId,
          email: customerEmail,
          firstName,
          fullName,
          lastName,
        },
      }
    }

    return {
      ...data,
      customer: {
        customerId,
        email: customerEmail,
        firstName: shippingAddress?.firstName,
        fullName: shippingAddress?.fullName,
        lastName: shippingAddress?.lastName,
      },
    }
  }
)

// Use the SKU to grab the PRODUCT info from CART.ITEMS
export const getProductModalInfo = (sku: string = '') =>
  createSelector([getCartItems], (cartItems): CartItem | Record<string, any> => {
    if (!sku) {
      return {}
    }

    return cartItems.find((item) => item.sku === sku) || {}
  })

// Gather INFO needed for all GTM data - used in hook =>
export const getGTMData = createSelector(
  [
    getCart,
    getCustomerId,
    getCustomerEmail,
    getUserPreferencesSelectedCurrency,
    getPagePath,
    getIsCheckoutValid,
    getPaymentDetails,
    isAuthenticated,
    getUserRole,
  ],
  (
    cart,
    customerId,
    email,
    selectedCurrency,
    pagePath,
    isCheckoutValid,
    paymentDetails,
    authenticated,
    userRole
  ): Record<string, any> => {
    const { currencyCode } = selectedCurrency
    return {
      authenticated,
      cart,
      currencyCode,
      customerId,
      email,
      isCheckoutValid,
      pagePath,
      paymentDetails,
      userRole,
    }
  }
)

// GA4 data layer event =>
export const getGA4Data = createSelector(
  [getCart, getCouponCode, getCartItems],
  (cart, couponCode, items) => {
    const { totalOrderPriceToPayCart, totalDiscountAmount } = cart
    return {
      ecommerce: {
        coupon: couponCode || '',
        currency: 'USD',
        items: buildGA4CartItems(items, couponCode),
        value: convertFromCentsToDollarsString(totalOrderPriceToPayCart - totalDiscountAmount),
      },
    }
  }
)

// Custom update cart data layer event =>
export const getUpdateCartEventData = createSelector(
  [getCart, getCouponCode, getCartItems],
  (cart, couponCode, items) => {
    const { totalOrderPriceToPayCart, totalDiscountAmount, itemsCartCount } = cart
    const cartItems = items.map((item) => {
      const {
        sku,
        productCategory,
        productName,
        price,
        artistFirstName,
        artistLastName,
        productType,
        artworkId,
      } = item
      const { discountPerUnit, subTotalPricePerUnit, quantity } = price
      return {
        item_sku: sku, // SKU
        item_art_id: `${artworkId}`, // artwork id
        item_title: productName, // artwork title
        coupon: couponCode,
        discount: convertFromCentsToDollarsString(discountPerUnit),
        artist_name: `${artistFirstName || ''} ${artistLastName || ''}`, // artist name
        item_category: capitalizeString(productCategory?.replace(/s$/, '')?.replace(/\sM/, 'm')), // category
        item_variant: productType === 'Original Work' ? 'original' : 'open',
        price: convertFromCentsToDollarsString(subTotalPricePerUnit), // price before discount
        ...(discountPerUnit !== 0 && {
          discounted_price:
            parseFloat(convertFromCentsToDollarsString(subTotalPricePerUnit)) -
            parseFloat(convertFromCentsToDollarsString(discountPerUnit)),
        }), // price AFTER discount
        quantity,
      }
    })
    return {
      cart: {
        subtotal: convertFromCentsToDollarsString(totalOrderPriceToPayCart - totalDiscountAmount),
        coupon: couponCode || '',
        quantity: itemsCartCount || 0,
        cart_items: cartItems || [],
      },
    }
  }
)
